import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Alert, CircularProgress, Link, Stack, Typography } from '@mui/material';
import { authApi } from '@/auth/authApi';
import { normalizeError } from '@/api/errors';
import { paths } from '@/routes/paths';

type Status = 'verifying' | 'success' | 'error' | 'missing';

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [status, setStatus] = useState<Status>(token ? 'verifying' : 'missing');
  const [message, setMessage] = useState('');
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    authApi
      .verifyEmail({ token })
      .then(() => setStatus('success'))
      .catch((error: unknown) => {
        setStatus('error');
        setMessage(normalizeError(error).message);
      });
  }, [token]);

  return (
    <Stack spacing={2} alignItems="flex-start">
      <Typography variant="h6">Email verification</Typography>
      {status === 'verifying' && (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={20} />
          <Typography variant="body2">Verifying your email…</Typography>
        </Stack>
      )}
      {status === 'success' && (
        <Alert severity="success">Your email has been verified. You can now sign in.</Alert>
      )}
      {status === 'error' && <Alert severity="error">{message || 'Verification failed.'}</Alert>}
      {status === 'missing' && (
        <Alert severity="error">This verification link is missing its token.</Alert>
      )}
      <Link component={RouterLink} to={paths.login} variant="body2" underline="hover">
        Go to sign in
      </Link>
    </Stack>
  );
}
